import { useState } from 'react';
import { getApiErrorMessage } from '@/api/errors';
import useProductListMutations from '@/api/mutations/useProductListMutations';
import useGetProductListQuery from '@/api/queries/useGetProductListQuery';
import Button from '@/components/Button';
import Confirm from '@/components/Confirm';
import CustomToast from '@/components/CustomToast';
import { ProductListUpdate } from '@/models/ProductList';
import Breadcrumbs from '@/modules/product/components/Breadcrumbs';
import ProductListDetailHeader from '@/modules/product/components/ProductListDetailHeader';
import ProductListForm from '@/modules/product/components/ProductListForm';
import ProductListLoadingState from '@/modules/product/components/ProductListLoadingState';
import ProductListUnavailableState from '@/modules/product/components/ProductListUnavailableState';
import { ProductListListPath, ProductListProductListPath } from '@/modules/product/paths';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { PlusIcon } from '@heroicons/react/24/solid';
import toast from 'react-hot-toast';
import { useNavigate, useParams } from 'react-router-dom';

const ProductListEdit = () => {
  const navigate = useNavigate();
  const { productListId } = useParams();
  const { isLoading, productList, error } = useGetProductListQuery(productListId);
  const { updateProductList, deleteProductList } = useProductListMutations();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const handleUpdate = async (productList: ProductListUpdate) => {
    if (!productListId) {
      return;
    }

    setErrorMessage(null);

    try {
      await updateProductList({ productListId, productList });

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Product List "${productList.name}" updated successfully!`}
          visible={t.visible}
        />
      ));
    } catch (caught) {
      setErrorMessage(
        await getApiErrorMessage(caught, 'Failed to update Product List. Please try again.'),
      );
    }
  };

  const handleDelete = async () => {
    if (!productList) {
      return;
    }

    try {
      await deleteProductList(productList.id);

      toast.custom((t) => (
        <CustomToast
          id={t.id}
          message={`Product List "${productList.name}" deleted.`}
          visible={t.visible}
        />
      ));

      navigate(ProductListListPath);
    } catch (caught) {
      const message = await getApiErrorMessage(
        caught,
        'Failed to delete Product List. Please try again.',
      );

      toast.custom((t) => (
        <CustomToast id={t.id} message={message} visible={t.visible} variant="error" />
      ));
    }
  };

  if (isLoading) {
    return <ProductListLoadingState label="Loading Product List" />;
  }

  if (error || !productList) {
    return <ProductListUnavailableState />;
  }

  return (
    <>
      <Breadcrumbs />
      <ProductListDetailHeader productList={productList}>
        <Button
          to={ProductListProductListPath.withProductListId(productList.id)}
          variant="secondary"
          icon={<PlusIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
        >
          Add products
        </Button>
        <Confirm
          onConfirm={handleDelete}
          title="Delete Product List"
          message={`Are you sure you want to delete product list "${productList.name}"? Its memberships and assignments will be removed. This action cannot be undone.`}
          confirmText="Delete"
          danger
        >
          {(handleClick, loading) => (
            <Button
              onClick={handleClick}
              icon={<XMarkIcon className="h-5 w-5 mr-1" aria-hidden="true" />}
              loading={loading}
              variant="delete"
            >
              Delete Product List
            </Button>
          )}
        </Confirm>
      </ProductListDetailHeader>

      <ProductListForm
        onSubmit={handleUpdate}
        productList={productList}
        errorMessage={errorMessage}
      />
    </>
  );
};

export default ProductListEdit;
